import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { DiaryRootState } from 'appRoot/rootReducer';
import { IBooking, BookingStatusNames, IBookingStatus, BookingMethods } from 'shared-types/index';
import moment from 'moment/moment';
import { LocaleService } from 'internal-services/locale/locale.service';
import { useAppDispatch } from 'app/appRoot/Store';
import { StatusServiceInstance } from 'external-services/status-service';
import { IBookingCardContainer, EditBookingProps } from './types';
import StaffNamePanelContainer from '../StaffNamePanelContainer';
import {
  quickValidationActions,
  saveQuickEditBooking,
} from 'internal-reducers/quickValidation/slice';
import { updateDelayedBookings } from 'internal-reducers/booking/slice';
import staffService from 'external-services/staff-service/staff.service'
import TablesLookupService from 'external-services/tables-lookup-service/tablesLookup.service';
import { cloneDeep, isEqual } from 'lodash-es';
import TagsSerivce from 'external-services/tags-service';
import { BookingCreatorState } from 'booking-creator/rootReducer';
import BookingNormaliseService from 'external-services/booking-normalise-service/bookingNormalise.service';
import BookingCardCompactV2 from 'design-v2/common/BookingCardCompact/BookingCardCompactV2';

const NS = 'BookingCardContainer';

export const useEditBookingValidation = (prop: EditBookingProps): boolean => {
  const staffInitialSettings = useSelector(
    (state: DiaryRootState) => state.staffValidationReducer,
    isEqual
  );
  const { staffEditRequired, staffEditEnabled, staffEdit = {} } = staffInitialSettings
  const result = staffService.checkValidityForStaffName(staffEditRequired, staffEditEnabled, staffEdit, staffEdit?.[prop])
  // console.debug('staffValidationSlice', {result})
  return result
};

const BookingCardContainer = (props: IBookingCardContainer) => {
  const {
    booking: oldBooking,
    handleBookingClicked,
    handleBookingStatusClicked,
    useDeprecatedStatusModal,
    hideTagsInBookingCard,
    parentWidth,
    mouseInteractions,
    handleBookingCardListPointerDown,
    draggedBookingId,
    isEditable = true,
    showDateInBookingCard = false
  } = props;
  const dispatch = useAppDispatch();

  const booking = useMemo(() => {
    return BookingNormaliseService.removeTablesStandbyBookingType(cloneDeep(oldBooking));
  }, [oldBooking]);

  const [unsavableBooking, setUnsavableBooking] = useState(booking);

  const recentChangeBookings = useSelector(
    (state: DiaryRootState) => state.bookingReducer.recentChangeBookings
  );
  const fullStatusOptions = useSelector(
    (state: DiaryRootState) => state.statusReducer.fullStatusOptions
  );

  // prettier-ignore
  const canSave = useSelector((state: DiaryRootState) => state.quickValidationReducer.canSave);
  // prettier-ignore
  const cantSaveMessageList = useSelector((state: DiaryRootState) => state.quickValidationReducer.cantSaveMessageList);
  // prettier-ignore
  const staffEnabled = useSelector((state: DiaryRootState) => state.settingReducer.generalSettings?.staffEditEnabled);
  // prettier-ignore
  const isWideVersion = useSelector((state: BookingCreatorState) => state.bookingCreatorSettingReducer.isWideVersion);
  const newStatus = useSelector((state: DiaryRootState) => state.quickValidationReducer.newStatus);
  // prettier-ignore
  const originStatus = useSelector((state: DiaryRootState) => state.quickValidationReducer.originStatus);
  const newPax = useSelector((state: DiaryRootState) => state.quickValidationReducer.newPax);
  // prettier-ignore
  const originPax= useSelector((state: DiaryRootState) => state.quickValidationReducer.originPax);
  // prettier-ignore
  const quickSaveAttempted = useSelector((state: DiaryRootState) => state.quickValidationReducer.quickSaveAttempted);

  const isStaffEnabled = staffEnabled && booking.method !== BookingMethods.walkin;
  const staffEditQuickEdit = useEditBookingValidation('staffEditQuickEdit')
  //prettier-ignore
  const enableEndBookingTime = useSelector((state: DiaryRootState) => state.settingReducer.generalSettings?.enableEndBookingTime);

  useEffect(() => {
    let recentlyChangedBooking = recentChangeBookings?.find((b) => b._id == booking._id);
    if (recentlyChangedBooking) {
      // NBI-8571 - recentChangeBookings finds from the list of bookings which does not contain the extra tags added - hence need to add the extra tags.
      TagsSerivce.getBookingTags(recentlyChangedBooking, true).then((tags) => {
        recentlyChangedBooking = { ...recentlyChangedBooking, tags: tags };
        setUnsavableBooking(
          BookingNormaliseService.removeTablesStandbyBookingType(cloneDeep(recentlyChangedBooking))
        );
      });
    }
  }, [recentChangeBookings]);

  useEffect(() => {
    if (unsavableBooking) {
      const tempBooking = cloneDeep(booking);
      TagsSerivce.getBookingTags(tempBooking).then((tags) => {
        tempBooking.tags = tags;
        setUnsavableBooking(tempBooking);
      });
    }
  }, [booking]);

  const filteredStatus = useMemo(() => {
    return StatusServiceInstance.getFilteredStatusList(
      booking.status?.name as any,
      booking as IBooking
    );
  }, [booking]);

  const startTime = useMemo(() => {
    const mom = moment(unsavableBooking.time);
    return {
      value: mom.format('h:mm'),
      suffix: mom.format('A'),
    };
  }, [unsavableBooking.time]);

  const endTime = useMemo(() => {
    const mom = moment(unsavableBooking.bookingEnd);
    return {
      value: mom.format('h:mm'),
      suffix: mom.format('A'),
    };
  }, [unsavableBooking.bookingEnd]);
  const bookingDate = useMemo(() => {
    return LocaleService.formatDate(unsavableBooking.time, 'medium-short');
  }, [unsavableBooking.time]);

  const selectedStatusIndex = useMemo(() => {
    return filteredStatus.findIndex(
      (statusItem) => statusItem.name === unsavableBooking.status.name
    );
  }, [filteredStatus, unsavableBooking?.status.name]);

  const handleStatus = (status: IBookingStatus, isQuick = false) => {
    if (!isQuick) {
      dispatch(
        quickValidationActions.clearValidationAndSetBothStatuses({
          status: unsavableBooking?.status,
          isIgnoreStaff: unsavableBooking.method === BookingMethods.walkin,
        })
      );
    } else dispatch(quickValidationActions.setNewStatus(status));
  };

  const handlePaxSave = async (covers: number) => {
    await dispatch(
      saveQuickEditBooking({
        isManualUpdate: true,
        booking: unsavableBooking,
        key: 'people',
        value: covers,
      })
    );

    dispatch(
      quickValidationActions.clearValidationAndSetBothStatuses({
        isIgnoreStaff: unsavableBooking.method === BookingMethods.walkin,
      })
    );
  };

  const handleStatusChange = async (booking: IBooking, status: BookingStatusNames) => {
    if (booking.status.name.toString() !== status.toString()) {
      await dispatch(
        updateDelayedBookings({
          delayedBooking: booking,
          changedProperty: 'status',
        })
      );
      await dispatch(
        saveQuickEditBooking({
          name: status,
          isManualUpdate: true,
          booking,
        })
      );
    }
  };

  // const handleCancelModal = () => {
  //   dispatch(quickValidationActions.clearValidation());
  // }

  const isRenderStaffPanel = useMemo(() => {
    return (Boolean(newStatus) && Boolean(originStatus) && newStatus.name !== originStatus.name) || originPax !== newPax
  }, [newStatus, originStatus, newPax, originPax]);

  const CommonStaffNamePanelRender = (): JSX.Element => {
    return <StaffNamePanelContainer isDisplay={isRenderStaffPanel} />;
  };

  const tableAvailable = useMemo(() => {
    return TablesLookupService.isTableAvailable(booking);
  }, [booking]);

  const handleSaveAttempted = () => {
    dispatch(quickValidationActions.setQuickSaveAttempted(true));
  };
  const handleMouseEnter = () => {
    if (mouseInteractions) {
      mouseInteractions.handleMouseEnter(unsavableBooking._id, unsavableBooking.tables.map(t => t._id));
    }
  }
  const handleMouseLeave = () => {
    if (mouseInteractions) {
      mouseInteractions.handleMouseLeave();
    }
  }
  const tableCapacity = useMemo(() => booking.tables.reduce((acc, t) => acc + t.capacity, 0), [booking.tables])

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}

      style={
        draggedBookingId === booking._id
          ? {
              opacity: 0.6,
            }
          : null
      }

    >
      <BookingCardCompactV2
        handleBookingCardListPointerDown={
          handleBookingCardListPointerDown
            ? (event) => handleBookingCardListPointerDown?.(booking, event)
            : null
        }
        customerId={booking?.customerId}
        bookingNotes={booking.notes}
        hasCustomerNotes={booking.hasCustomerNotes}
        isEditable={isEditable}
        parentWidth={parentWidth}
        isModalStyle={true}
        showDateInBookingCard={showDateInBookingCard}
        startTimeValue={startTime.value}
        startTimePeriod={startTime.suffix}
        endTimeValue={endTime.value}
        endTimePeriod={endTime.suffix}
        company={`${unsavableBooking.company || ''}`}
        fullName={`${unsavableBooking.firstName || ''} ${unsavableBooking.lastName || ''}`}
        tablesNames={TablesLookupService.getTableNamesPrimaryFirst(unsavableBooking.tables, unsavableBooking.primaryTableId)}
        covers={unsavableBooking.people}
        tags={unsavableBooking.tags}
        functionTracker={booking.partner}
        selectedIndex={selectedStatusIndex}
        bookingStatusList={filteredStatus}
        historicalBookingStatus={unsavableBooking.status}
        handleStatusChange={async (status: string) => {
          await handleStatusChange(booking, status as BookingStatusNames);
        }}
        tableAvailable={tableAvailable}
        handleBookingClicked={() => {
          /**
           * don't pass the actual 'unsavableBooking' into handleBookingClicked, as this has lots of modifications
           * that can't be saved (such as invalid tags, like "locked", "notes", etc - these are not real booking tags
           * they are just used for displaying icons).
           */
          handleBookingClicked(unsavableBooking._id);
        }}
        bookingDate={bookingDate}
        handlePaxSave={handlePaxSave}
        canSave={canSave}
        cantSaveMessageList={cantSaveMessageList}
        staffRequired={isStaffEnabled}
        handleStatus={handleStatus}
        StaffPanel={CommonStaffNamePanelRender}
        isWideVersion={isWideVersion}
        hideTagsInBookingCard={hideTagsInBookingCard}
        isQuickEditLocked={unsavableBooking.locked}
        saveAttempted={quickSaveAttempted}
        handleSaveAttempted={handleSaveAttempted}
        handleChangePax={(newPax) => dispatch(quickValidationActions.setNewPax(newPax))}
        handleOpenPaxModal={(originPax) => {
          handleStatus(null, false);
          dispatch(quickValidationActions.setOriginPax(originPax));
        }}
        handleClosePaxModal={() => dispatch(quickValidationActions.setQuickSaveAttempted(false))}
        bookingType={unsavableBooking.bookingType}
        paymentDetails={unsavableBooking.paymentDetails}
        staffEditQuickEdit={staffEditQuickEdit}
        handleBookingStatusClicked={(statusId: string) =>
          handleBookingStatusClicked({ statusId, bookingId: booking._id })
        }
        useDeprecatedStatusModal={useDeprecatedStatusModal}
        isOverTableCapacity={booking.tables.length ? booking.people > tableCapacity : false}
        enableEndBookingTime={enableEndBookingTime}
      />
    </div>
  );
};

export default BookingCardContainer;
